import { css } from '@linaria/core';
import Container from './Container';
import { PulseLoader } from 'react-spinners';
import { useMediaQuery } from 'react-responsive';
import React, {
  useState,
  createContext,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { useSearch } from '@elastic/react-search-ui';
import AppliedFilters from './AppliedFilters';
import FilterValidator from './FilterValidator';
import MapFilterLogger from './MapFilterLogger';
import classNames from 'classnames';
import { useMapState } from '../contexts/MapStateContext';
import {
  LAYOUT_FILTER_FIELD,
  isHiddenAppliedFilter,
  isUiOnlyFilter,
} from '../utils/uiFilters';
import { isMapFilter } from '../utils/mapFilters';

const layout = css`
  display: flex;
  align-items: center;
  justify-content: center;
`;
const container = css`
  display: flex;
  justify-content: space-between;
  gap: 130px;

  @media (min-width: 1080px) {
    flex: 1;
    gap: 60px;
  }

  @media (min-width: 1400px) {
    flex: 1;
    gap: 130px;
  }
`;
const sidebar = css`
  @media (min-width: 800px) {
    width: 510px;
  }
  @media (min-width: 1080px) {
    width: 280px;
  }
  @media (min-width: 1400px) {
    width: 313px;
  }
`;
const filtersHeading = css`
  margin: 0 0 20px 0;
  padding-bottom: 20px;
  border-bottom: solid 1px #e5dbd1;
  font-size: 28px;
  line-height: 40px;
`;
const filtersAppliedFilters = css`
  @media (min-width: 1080px) {
    margin-bottom: 10px;
  }
`;
const body = css`
  width: 100%;

  @media (min-width: 1080px) {
    flex: 1;
  }
`;
const loadingWrapper = css`
  display: flex;
  justify-content: center;
`;
const appliedFiltersMargin = css`
  margin-bottom: -20px;
`;

const filtersHeadingNoFilters = css`
  @media (min-width: 1080px) {
    margin: 0 0 -10px 0;
  }
`;

const LayoutModeContext = createContext();

export const useLayoutMode = () => useContext(LayoutModeContext);

let persistedLayoutMode = null;

export const LayoutModeProvider = ({ children, searchType }) => {
  const { filters, setFilter } = useSearch();
  // The map is the default layout for the heritage-gateway search only; every
  // other search defaults to the list. Once the user picks a layout it is
  // remembered (persistedLayoutMode) across provider remounts in the session.
  const defaultLayoutMode = searchType === 'heritage-gateway' ? 'map' : 'list';
  const [layoutMode, setLayoutModeState] = useState(
    persistedLayoutMode ?? defaultLayoutMode,
  );

  const isDesktop = useMediaQuery({ query: '(min-width: 1080px)' });

  const availableLayouts = useMemo(() => {
    const layouts = ['list'];

    if (isDesktop && searchType !== 'heritage-gateway') {
      layouts.push('grid');
    }

    const rootElement = document.getElementById('root');
    const showMap = rootElement?.hasAttribute('data-search-show-map');

    if (
      (searchType === 'har' ||
        searchType === 'nhle' ||
        searchType === 'heritage-gateway') &&
      showMap
    ) {
      layouts.push('map');
    }

    return layouts;
  }, [isDesktop, searchType]);

  // Read layout from filter state (URL) on mount / filter change
  useEffect(() => {
    const layoutFilter = filters.find(f => f.field === LAYOUT_FILTER_FIELD);
    const filterValue = layoutFilter?.values?.[0];
    if (filterValue && availableLayouts.includes(filterValue)) {
      setLayoutModeState(filterValue);
    }
  }, [filters, availableLayouts]);

  // Seed the layout filter when map is the active mode but no layoutView filter
  // is set yet — e.g. map as the default view, or a provider remount where the
  // persisted mode is map but the fresh Search UI state has no filter. The
  // connector keys mapSearch=true off this filter, so without it the map search
  // omits mapSearch. Scoped to map only so the list path is left untouched.
  useEffect(() => {
    if (layoutMode !== 'map' || !availableLayouts.includes('map')) return;
    const hasLayoutFilter = filters.some(f => f.field === LAYOUT_FILTER_FIELD);
    if (!hasLayoutFilter) {
      setFilter(LAYOUT_FILTER_FIELD, 'map', 'all');
    }
  }, [filters, availableLayouts, layoutMode, setFilter]);

  // Fallback when current mode isn't in available layouts
  useEffect(() => {
    if (!availableLayouts.includes(layoutMode)) {
      setLayoutModeState('list');
      setFilter(LAYOUT_FILTER_FIELD, 'list', 'all');
    }
  }, [layoutMode, availableLayouts, setFilter]);

  const handleSetLayoutMode = newMode => {
    persistedLayoutMode = newMode;
    setLayoutModeState(newMode);
    setFilter(LAYOUT_FILTER_FIELD, newMode, 'all');
  };

  return (
    <LayoutModeContext.Provider
      value={{
        layoutMode,
        setLayoutMode: handleSetLayoutMode,
        availableLayouts,
        searchType,
      }}
    >
      {children}
    </LayoutModeContext.Provider>
  );
};

function BodySection({
  BodyHeader,
  BodyContent,
  BodyFooter,
  totalResults,
  isLoading,
  wasSearched,
  NoResults,
}) {
  const { layoutMode } = useLayoutMode();
  const isMapMode = layoutMode === 'map';

  if (totalResults === 0 && !isLoading && wasSearched && !isMapMode) {
    return NoResults;
  }

  if (totalResults > 0 || isMapMode) {
    return (
      <>
        {BodyHeader}
        {BodyContent}
        {BodyFooter}
      </>
    );
  }

  return null;
}

export default function Layout({
  BodyContent,
  BodyFooter,
  BodyHeader,
  Filters,
  filters,
  Header,
  isLoading,
  NoResults,
  searchType,
  totalResults,
  wasSearched,
}) {
  const isDesktop = useMediaQuery({ query: '(min-width: 1080px)' });
  const isNhle = searchType === 'nhle';
  const isArchive = searchType === 'archive';
  const isGateway = searchType === 'heritage-gateway';

  // A drawn shape counts as an applied filter (the "Selected area" chip);
  // viewport and UI-only filters do not.
  const hasShapeFilter = filters.some(f => f.field === 'mapShapeType');
  const hasAppliedFilters =
    hasShapeFilter ||
    filters.some(
      f =>
        !isUiOnlyFilter(f.field) &&
        !isMapFilter(f.field) &&
        !isHiddenAppliedFilter(f.field),
    );

  const { mapState } = useMapState();
  // The enlarged map (hidden sidebar) only applies to the map layout; other
  // views always show the filters.
  const isMapLayout =
    filters.find(f => f.field === LAYOUT_FILTER_FIELD)?.values?.[0] === 'map';
  const showSidebar = isDesktop && !(isMapLayout && mapState.sidebarHidden);

  return (
    <LayoutModeProvider searchType={searchType}>
      <FilterValidator />
      <MapFilterLogger />
      {Header}
      <div className={layout}>
        <Container className={container}>
          {showSidebar && (
            <div className={sidebar}>
              <h2
                className={classNames(
                  filtersHeading,
                  (isNhle || isArchive || isGateway) &&
                    !hasAppliedFilters &&
                    filtersHeadingNoFilters,
                )}
              >
                {hasAppliedFilters ? 'Applied Filters' : 'Filters'}
              </h2>

              <div
                className={classNames(
                  filtersAppliedFilters,
                  (isNhle || isArchive || isGateway) &&
                    hasAppliedFilters &&
                    appliedFiltersMargin,
                )}
              >
                <AppliedFilters />
              </div>

              {Filters}
            </div>
          )}
          <div className={body}>
            {isLoading && !wasSearched && (
              <div className={loadingWrapper}>
                <PulseLoader color="#1a1a1a" />
              </div>
            )}

            <BodySection
              BodyContent={BodyContent}
              BodyFooter={BodyFooter}
              BodyHeader={BodyHeader}
              NoResults={NoResults}
              isLoading={isLoading}
              totalResults={totalResults}
              wasSearched={wasSearched}
            />
          </div>
        </Container>
      </div>
    </LayoutModeProvider>
  );
}
